import { Component, inject } from '@angular/core';
import { CommonModule, UpperCasePipe } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { UtilityService } from '@/layout/service/utility.service';
import { TranslationService } from '@/core/i18n/translation.service';

@Component({
    selector: 'app-excel-error-dialog',
    standalone: true,
    imports: [CommonModule, UpperCasePipe, DialogModule, ButtonModule, TableModule],
    templateUrl: './excel-error-dialog.component.html',
    styleUrl: './excel-error-dialog.component.scss',
})
export class ExcelErrorDialogComponent {
    private readonly utilityService = inject(UtilityService);
    private readonly translationService = inject(TranslationService);
    readonly t = this.translationService.translate.bind(this.translationService);

    readonly visible = this.utilityService.excelErrorVisible;
    readonly heading = this.utilityService.excelErrorHeading;
    readonly message = this.utilityService.excelErrorMessage;
    readonly errors = this.utilityService.excelErrorData;
    readonly columns = this.utilityService.excelErrorColumns;

    handleVisibleChange(visible: boolean): void {
        if (!visible) {
            this.utilityService.closeExcelErrors();
        }
    }

    onHide(): void {
        this.utilityService.closeExcelErrors();
    }

    async downloadExcel(): Promise<void> {
        const ExcelJS = await import('exceljs');
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Errors');

        const cols = this.columns();
        const headerLabels = cols.map(c => c.charAt(0).toUpperCase() + c.slice(1));
        const headerRow = worksheet.addRow(headerLabels);

        headerRow.font = { bold: true };
        headerRow.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFDDEBF7' },
        };

        this.errors().forEach(error => {
            worksheet.addRow(cols.map(col => error[col] ?? ''));
        });

        cols.forEach((_, idx) => {
            worksheet.getColumn(idx + 1).width = 32;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'upload-errors.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
}
